import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Box, Tabs, Tab, CircularProgress, Alert, IconButton, Tooltip, AppBar, Toolbar } from '@mui/material';
import { Settings, LogOut, Sun, Moon } from 'lucide-react';
import { usePortfolioCompany, useTenant, useFund, getPreviousPeriod, useSignOut } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import { useUIStore } from '../stores/useUIStore';
import { SubmitMetrics } from '../components/portco/SubmitMetrics';
import { SubmissionHistory } from '../components/portco/SubmissionHistory';
import { MetricsDownload } from '../components/portco/MetricsDownload';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

export function PortCoDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { themeMode, toggleTheme } = useUIStore();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const { data: company, isLoading: companyLoading } = usePortfolioCompany(user?.portfolio_company_id || '');
  const { data: tenant } = useTenant(user?.tenant_id || '');
  const { data: fund } = useFund(company?.fund_id || '');

  const currentPeriod = fund?.reporting_frequency ? getPreviousPeriod(fund.reporting_frequency, fund.reporting_timezone || 'UTC') : null;

  const handleSignOut = async () => {
    await signOut.mutateAsync();
    navigate('/login');
  };

  if (userLoading || companyLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !company || !tenant || !fund) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Unable to load company information</Alert>
      </Box>
    );
  }

  if (!currentPeriod) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Your fund does not have a reporting schedule configured. Please contact your fund administrator.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 3 }}>
            <img src="/logo.svg" alt="Diggens" style={{ height: '24px', width: 'auto' }} />
            <Typography variant="h6" noWrap>
              Diggens
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ flexGrow: 1, color: 'text.secondary' }}>
            {company.name}
          </Typography>

          <Tooltip title="Settings">
            <IconButton onClick={() => navigate('/settings')} size="small">
              <Settings size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Tooltip>

          <Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton onClick={toggleTheme} size="small">
              {themeMode === 'light' ? <Moon size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Sun size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Sign out">
            <IconButton onClick={handleSignOut} size="small" disabled={signOut.isPending}>
              <LogOut size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          {company.name}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {company.sector && `${company.sector} • `}
          Reporting {fund.reporting_frequency} on the {fund.reporting_day_of_month}
          {fund.reporting_day_of_month === 1 ? 'st' : fund.reporting_day_of_month === 2 ? 'nd' : fund.reporting_day_of_month === 3 ? 'rd' : 'th'}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Submit" />
            <Tab label="History" />
            <Tab label="Download" />
          </Tabs>
        </Box>

        {activeTab === 0 && <SubmitMetrics companyId={company.id} tenant={tenant} currentPeriod={currentPeriod} />}
        {activeTab === 1 && <SubmissionHistory companyId={company.id} />}
        {activeTab === 2 && <MetricsDownload companyId={company.id} fund={fund} currentPeriod={currentPeriod} />}
      </Box>
    </Box>
  );
}
